import React from 'react';
import { Container, Typography, Box, Link as MuiLink, Button, useTheme, Paper, Grid, Chip } from '@mui/material';
import { Link } from 'react-router-dom';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import GroupIcon from '@mui/icons-material/Group';
  
export default function AboutPage() {
  const theme = useTheme();
  return (
    <Box
      sx={{
        position: 'relative',
        minHeight: '100vh',
        background: `linear-gradient(135deg, ${theme.palette.primary.light}15 0%, ${theme.palette.secondary.light}15 100%)`,
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'flex-start',
        textAlign: 'center',
        pt: 1,
      }}
    >
      <Container
        maxWidth="lg"
        sx={{
          position: 'relative',
          zIndex: 2,
          backgroundColor: 'rgba(255, 255, 255, 0.95)',
          p: { xs: 3, md: 4 },
          borderRadius: 4,
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
          width: '100%',
          mx: 2,
          mt: { xs: 4, md: 8 },
          mb: { xs: 4, md: 8 },
        }}
      >
        <Typography 
          variant="h3" 
          component="h1" 
          gutterBottom
          sx={{
            fontWeight: 'bold',
            background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            mb: 2
          }}
        >
          Welcome to mycatch
        </Typography>
        <Typography 
          variant="h6" 
          color="text.secondary" 
          gutterBottom
          sx={{ mb: 4 }}
        >
          Where anglers share their best catches and compete for glory!
        </Typography>

        {/* Feature Highlights */}
        <Grid container spacing={3} sx={{ mb: 6 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Paper 
              elevation={0}
              sx={{ 
                p: 3, 
                borderRadius: 3,
                backgroundColor: `${theme.palette.primary.light}15`,
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                transition: 'transform 0.2s',
                '&:hover': {
                  transform: 'translateY(-5px)',
                }
              }}
            >
              <EmojiEventsIcon sx={{ fontSize: 40, color: theme.palette.primary.main, mb: 2 }} />
              <Typography variant="h6" gutterBottom>Monthly Competitions</Typography>
              <Typography variant="body2" color="text.secondary">
                Show off your best catches and win prizes
              </Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Paper 
              elevation={0}
              sx={{ 
                p: 3, 
                borderRadius: 3,
                backgroundColor: `${theme.palette.secondary.light}15`,
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                transition: 'transform 0.2s',
                '&:hover': {
                  transform: 'translateY(-5px)',
                }
              }}
            >
              <PhotoCameraIcon sx={{ fontSize: 40, color: theme.palette.secondary.main, mb: 2 }} />
              <Typography variant="h6" gutterBottom>Share Your Catches</Typography>
              <Typography variant="body2" color="text.secondary">
                Upload and showcase your fishing adventures
              </Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Paper 
              elevation={0}
              sx={{ 
                p: 3, 
                borderRadius: 3,
                backgroundColor: `${theme.palette.success.light}15`,
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                transition: 'transform 0.2s',
                '&:hover': {
                  transform: 'translateY(-5px)',
                }
              }}
            >
              <LocationOnIcon sx={{ fontSize: 40, color: theme.palette.success.main, mb: 2 }} />
              <Typography variant="h6" gutterBottom>Find New Spots</Typography>
              <Typography variant="body2" color="text.secondary">
                Discover great fishing locations near you
              </Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Paper 
              elevation={0}
              sx={{ 
                p: 3, 
                borderRadius: 3,
                backgroundColor: `${theme.palette.info.light}15`,
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                transition: 'transform 0.2s',
                '&:hover': {
                  transform: 'translateY(-5px)',
                }
              }}
            >
              <GroupIcon sx={{ fontSize: 40, color: theme.palette.info.main, mb: 2 }} />
              <Typography variant="h6" gutterBottom>Join the Community</Typography>
              <Typography variant="body2" color="text.secondary">
                Connect with fellow fishing enthusiasts
              </Typography>
            </Paper>
          </Grid>
        </Grid>

        {/* What's the Catch Section */}
        <Box sx={{ mt: 6, textAlign: 'left' }}>
          <Typography 
            variant="h5" 
            component="h2" 
            gutterBottom
            sx={{ 
              display: 'flex', 
              alignItems: 'center',
              gap: 1,
              color: theme.palette.primary.main
            }}
          >
            What's the Catch?
          </Typography>
          <Typography variant="body1" color="text.secondary" paragraph>
            mycatch is your go-to spot for sharing epic fishing moments and competing in monthly photo contests. 
            Show off your biggest catches, discover new fishing spots, and connect with fellow anglers who share your passion.
          </Typography>
          <Box sx={{ mt: 3 }}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Paper 
                  elevation={0}
                  sx={{ 
                    p: 2, 
                    borderRadius: 2,
                    backgroundColor: `${theme.palette.background.paper}`,
                    border: `1px solid ${theme.palette.divider}`,
                  }}
                >
                  <Typography variant="subtitle1" gutterBottom>
                    🏆 Monthly Competitions
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Enter our "Catch of the Month" contest and win bragging rights
                  </Typography>
                </Paper>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Paper 
                  elevation={0}
                  sx={{ 
                    p: 2, 
                    borderRadius: 2,
                    backgroundColor: `${theme.palette.background.paper}`,
                    border: `1px solid ${theme.palette.divider}`,
                  }}
                >
                  <Typography variant="subtitle1" gutterBottom>
                    📸 Share Your Story
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Upload your best catches and fishing adventures
                  </Typography>
                </Paper>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Paper 
                  elevation={0}
                  sx={{ 
                    p: 2, 
                    borderRadius: 2,
                    backgroundColor: `${theme.palette.background.paper}`,
                    border: `1px solid ${theme.palette.divider}`,
                  }}
                >
                  <Typography variant="subtitle1" gutterBottom>
                    🎣 Discover New Spots
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Find great fishing locations and learn new techniques
                  </Typography>
                </Paper>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Paper 
                  elevation={0}
                  sx={{ 
                    p: 2, 
                    borderRadius: 2,
                    backgroundColor: `${theme.palette.background.paper}`,
                    border: `1px solid ${theme.palette.divider}`,
                  }}
                >
                  <Typography variant="subtitle1" gutterBottom>
                    💰 Earn from Your Knowledge
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Share your favorite spots and earn from your expertise
                  </Typography>
                </Paper>
              </Grid>
            </Grid>
          </Box>
        </Box>

        {/* Why Anglers Love Us Section */}
        <Box sx={{ mt: 6, textAlign: 'left' }}>
          <Typography 
            variant="h5" 
            component="h2" 
            gutterBottom
            sx={{ 
              display: 'flex', 
              alignItems: 'center',
              gap: 1,
              color: theme.palette.secondary.main
            }}
          >
            Why Anglers Love Us
          </Typography>
          <Typography variant="body1" color="text.secondary" paragraph>
            We've built a platform that's all about making fishing more fun and rewarding:
          </Typography>
          <Box sx={{ mt: 3 }}>
            <Grid container spacing={2}>
              {[
                { icon: '🎯', text: 'Easy-to-use interface that lets you focus on what matters - your catches' },
                { icon: '🏆', text: 'Monthly competitions to keep the excitement going' },
                { icon: '🔒', text: 'Smart location sharing that respects your privacy' },
                { icon: '💎', text: 'Fair and transparent way to earn from your fishing knowledge' }
              ].map((item, index) => (
                <Grid item xs={12} key={index}>
                  <Paper 
                    elevation={0}
                    sx={{ 
                      p: 2, 
                      borderRadius: 2,
                      backgroundColor: `${theme.palette.background.paper}`,
                      border: `1px solid ${theme.palette.divider}`,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 2
                    }}
                  >
                    <Typography variant="h4">{item.icon}</Typography>
                    <Typography variant="body1">{item.text}</Typography>
                  </Paper>
                </Grid>
              ))}
            </Grid>
          </Box>
        </Box>

        {/* Our Promise Section */}
        <Box sx={{ mt: 6, textAlign: 'left' }}>
          <Typography 
            variant="h5" 
            component="h2" 
            gutterBottom
            sx={{ 
              display: 'flex', 
              alignItems: 'center',
              gap: 1,
              color: theme.palette.success.main
            }}
          >
            Our Promise to You
          </Typography>
          <Typography variant="body1" color="text.secondary" paragraph>
            We're all about making fishing more awesome by:
          </Typography>
          <Box sx={{ mt: 3 }}>
            {[
              { icon: '🤝', title: 'Keeping it real', text: 'Honest catches, real locations, genuine anglers' },
              { icon: '👥', title: 'Building community', text: 'Helping each other catch more and better fish' },
              { icon: '🎉', title: 'Making it fun', text: 'Regular competitions and challenges to keep things exciting' }
            ].map((item, index) => (
              <Paper 
                key={index}
                elevation={0}
                sx={{ 
                  p: 2, 
                  mb: 2,
                  borderRadius: 2,
                  backgroundColor: `${theme.palette.background.paper}`,
                  border: `1px solid ${theme.palette.divider}`,
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Typography variant="h4">{item.icon}</Typography>
                  <Box>
                    <Typography variant="subtitle1" color="primary">
                      {item.title}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {item.text}
                    </Typography>
                  </Box>
                </Box>
              </Paper>
            ))}
          </Box>
        </Box>

        {/* CTA Section */}
        <Box sx={{ mt: 6, textAlign: 'center' }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            By using mycatch, you agree to our{' '}
            <MuiLink component={Link} to="/terms" sx={{ color: 'primary.main' }}>
              Terms and Conditions
            </MuiLink>
            .
          </Typography>
          <Button
            variant="contained"
            color="primary"
            component={Link}
            to="/images/view"
            sx={{ 
              borderRadius: 2, 
              textTransform: 'none', 
              px: 4, 
              py: 1.5,
              fontSize: '1.1rem',
              boxShadow: '0 4px 14px rgba(0, 0, 0, 0.1)',
              '&:hover': {
                transform: 'translateY(-2px)',
                boxShadow: '0 6px 20px rgba(0, 0, 0, 0.15)',
              }
            }}
          >
            Join Our Community
          </Button>
        </Box>
      </Container>
    </Box>
  );
}
